import type { Customer, CustomerInput } from '@iobiz/shared'
import { uuidv7 } from '@iobiz/shared'
import { dbFor, type LocalRow } from '../db.js'
import { enqueue, runCycle } from '../sync/engine.js'
import { toCustomer } from './customers.local.js'

/**
 * Écritures clients en mode local-first (§7.6).
 *
 * ⭐ Le formulaire n'attend PLUS le réseau. On écrit dans la réplique, on met
 * la mutation en file, et on rend la main immédiatement. Que la boutique soit
 * connectée ou non ne change rien au geste de l'utilisateur — c'est toute la
 * promesse du projet.
 */

/** Forme « colonnes » attendue par la réplique, à partir d'une saisie validée. */
function toRow(id: string, input: CustomerInput, version: number): LocalRow {
  return {
    id,
    code: input.code ?? null,
    type: input.type,
    first_name: input.firstName ?? null,
    last_name: input.lastName ?? null,
    company_name: input.companyName ?? null,
    tax_id: input.taxId ?? null,
    birth_date: input.birthDate ?? null,
    email: input.email ?? null,
    phone: input.phone ?? null,
    mobile: input.mobile ?? null,
    contact_first_name: input.contactFirstName ?? null,
    contact_last_name: input.contactLastName ?? null,
    contact_role: input.contactRole ?? null,
    contact_phone: input.contactPhone ?? null,
    contact_email: input.contactEmail ?? null,
    address_line1: input.addressLine1 ?? null,
    postal_code: input.postalCode ?? null,
    city: input.city ?? null,
    country_code: input.countryCode ?? null,
    payment_terms_days: input.paymentTermsDays ?? 0,
    notes: input.notes ?? null,
    tags: input.tags ?? [],
    avatar_url: input.avatarUrl ?? null,
    version,
  }
}

/**
 * Champs RÉELLEMENT modifiés, et eux seuls.
 *
 * ⚠️ C'est la pièce maîtresse de la fusion. Envoyer l'objet entier ferait de
 * chaque modification concurrente une collision sur tous les champs, et
 * l'utilisateur passerait son temps à arbitrer des conflits imaginaires.
 * En n'envoyant que le delta, deux personnes peuvent corriger la même fiche
 * — l'une le téléphone, l'autre l'adresse — sans jamais se gêner.
 */
export function diffInput(
  before: Customer | null | undefined,
  input: CustomerInput,
): Record<string, unknown> {
  if (!before) return { ...input }

  const previous: Record<string, unknown> = {
    type: before.type,
    code: before.code,
    firstName: before.firstName,
    lastName: before.lastName,
    companyName: before.companyName,
    taxId: before.taxId,
    birthDate: before.birthDate,
    email: before.email,
    phone: before.phone,
    mobile: before.mobile,
    addressLine1: before.addressLine1,
    postalCode: before.postalCode,
    city: before.city,
    countryCode: before.countryCode,
    paymentTermsDays: before.paymentTermsDays,
    notes: before.notes,
    tags: before.tags,
    avatarUrl: before.avatarUrl,
  }

  const delta: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (key === 'id') continue
    const old = previous[key]
    // `null` et `undefined` désignent tous deux « pas de valeur » : les
    // distinguer produirait des différences fantômes à chaque enregistrement.
    const sameEmpty = (old ?? null) === null && (value ?? null) === null
    if (sameEmpty) continue
    if (JSON.stringify(old ?? null) !== JSON.stringify(value ?? null)) delta[key] = value
  }
  return delta
}

/**
 * Enregistre un client : réplique d'abord, file d'attente ensuite.
 *
 * Renvoie la fiche telle qu'elle apparaît localement — avec `_dirty`, donc
 * protégée de l'écrasement par le prochain delta descendant.
 */
export async function saveCustomer(
  storeId: string,
  input: CustomerInput,
  existing?: Customer | null,
): Promise<Customer> {
  const db = dbFor(storeId)
  // L'identifiant est généré ICI (ADR-11) : hors ligne, l'appareil doit
  // pouvoir référencer la fiche avant tout aller-retour avec le serveur.
  const id = existing?.id ?? input.id ?? uuidv7()
  const version = existing?.version ?? 0

  const delta = diffInput(existing, input)
  // Rien n'a changé : ni écriture locale, ni mutation. Enregistrer un
  // formulaire ouvert puis refermé ne doit rien produire.
  if (existing && Object.keys(delta).length === 0) return existing

  const now = new Date().toISOString()
  const row = {
    ...toRow(id, input, version),
    created_at: existing?.createdAt ?? now,
    updated_at: now,
    last_activity_at: existing?.lastActivityAt ?? null,
    _dirty: 1 as const,
    _localOnly: existing ? (0 as const) : (1 as const),
  }
  await db.customers.put(row)

  await enqueue(storeId, {
    entity: 'customer',
    entityId: id,
    op: existing ? 'update' : 'create',
    payload: existing ? delta : { ...input, id },
    baseVersion: existing ? existing.version : null,
  })

  // Tentative immédiate : hors ligne elle échouera sans bruit et la file
  // reprendra au retour du réseau.
  void runCycle(storeId)

  return toCustomer(row)
}

export async function removeCustomer(
  storeId: string,
  customer: Customer,
): Promise<void> {
  const db = dbFor(storeId)
  await db.customers.delete(customer.id)

  /**
   * ⭐ Les BROUILLONS rattachés partent aussi, ici comme sur le serveur.
   *
   * ⚠️ Sans ce retrait local, ils resteraient affichés jusqu'à la prochaine
   * synchronisation, rattachés à un client qui n'existe plus — l'utilisateur
   * conclurait que la suppression n'a pas fonctionné. Les documents VALIDÉS,
   * eux, sont conservés des deux côtés : ils portent un numéro légal.
   */
  const brouillons = await db.transactions.toArray()
  for (const ligne of brouillons) {
    const row = ligne as unknown as Record<string, unknown>
    if (row['customer_id'] === customer.id && row['status'] === 'draft') {
      await db.transactions.delete(String(row['id']))
    }
  }

  await enqueue(storeId, {
    entity: 'customer',
    entityId: customer.id,
    op: 'delete',
    payload: {},
    baseVersion: customer.version,
  })
  void runCycle(storeId)
}
